import {
  FOIL_SHEEN,
  FOIL_TEXTURE_URLS,
  foilPatternStyle,
  pickFoilTexture,
} from "@/components/cards/foil/foil-config";
import type {
  FoilConfig,
  FoilTexture,
  FoilTint,
} from "@/components/cards/foil/foil-config";

// Foil + glare overlays are driven by the CSS vars set by TiltCard
// (--mx/--my pointer position, --o hover intensity).

const FoilLayer = ({
  texture = "illusion",
  tint = "rainbow",
}: {
  texture?: FoilTexture;
  tint?: FoilTint;
}) => (
  <>
    {/* Pattern image — whisper only (illusion lines etc. must not dominate art). */}
    <div
      className="z-20 absolute inset-0 pointer-events-none"
      style={{
        ...foilPatternStyle(texture),
        opacity: "calc(0.015 + var(--o, 0) * 0.04)",
        transition: "opacity 200ms",
      }}
    />
    {/* Moving sheen — the actual foil effect. */}
    <div
      className="z-20 absolute inset-0 pointer-events-none"
      style={{
        backgroundImage: FOIL_SHEEN[tint],
        backgroundSize: "300% 300%",
        backgroundPosition: "var(--mx, 50%) var(--my, 50%)",
        // void = black chrome: overlay keeps dark body + silver edge flash readable
        mixBlendMode: tint === "void" ? "overlay" : "soft-light",
        opacity:
          tint === "void"
            ? "calc(0.18 + var(--o, 0) * 0.34)"
            : "calc(0.12 + var(--o, 0) * 0.28)",
        transition: "opacity 200ms",
      }}
    />
    {/* glitter sparkle */}
    <div
      className="z-20 absolute inset-0 pointer-events-none"
      style={{
        backgroundImage: `url(${FOIL_TEXTURE_URLS.glitter})`,
        backgroundSize: "45%",
        backgroundPosition: "calc(100% - var(--mx, 50%)) calc(100% - var(--my, 50%))",
        mixBlendMode: "soft-light",
        opacity: "calc(0.02 + var(--o, 0) * 0.08)",
        transition: "opacity 200ms",
      }}
    />
  </>
);

// Border-only foil: the effect is masked to just the outer margin ring of the
// card (like a premium TCG border).
const BORDER_WIDTH = 7; // px ring thickness

const BORDER_MASK: React.CSSProperties = {
  padding: BORDER_WIDTH,
  WebkitMask: "linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0)",
  mask: "linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0)",
};

const GOLD_BAND =
  "linear-gradient(135deg, #6b5210 0%, #f7e08a 22%, #b98b2b 40%, #fff4c2 55%, #9c7418 72%, #f7e08a 88%, #6b5210 100%)";
const SILVER_BAND =
  "linear-gradient(135deg, #5a5f6b 0%, #e8edf5 22%, #8b93a3 40%, #ffffff 55%, #767d8c 72%, #dfe4ee 88%, #5a5f6b 100%)";

const BorderFoilLayer = ({
  metal,
  tint = "rainbow",
}: {
  metal?: "gold" | "silver" | undefined;
  tint?: FoilTint;
}) => (
  <>
    {metal && (
      <div
        className="z-20 absolute inset-0 rounded-xl pointer-events-none"
        style={{
          ...BORDER_MASK,
          background: metal === "gold" ? GOLD_BAND : SILVER_BAND,
          backgroundSize: "300% 300%",
          backgroundPosition: "var(--mx, 50%) var(--my, 50%)",
          opacity: "calc(0.75 + var(--o, 0) * 0.25)",
        }}
      />
    )}
    <div
      className="z-20 absolute inset-0 rounded-xl pointer-events-none"
      style={{
        ...BORDER_MASK,
        backgroundImage: FOIL_SHEEN[tint],
        backgroundSize: "300% 300%",
        backgroundPosition: "var(--mx, 50%) var(--my, 50%)",
        mixBlendMode: "soft-light",
        opacity: "calc(0.35 + var(--o, 0) * 0.4)",
        transition: "opacity 200ms",
      }}
    />
  </>
);

export const GlareLayer = () => (
  <div
    className="z-30 absolute inset-0 pointer-events-none"
    style={{
      background:
        "radial-gradient(circle at var(--mx, 50%) var(--my, 50%), rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.05) 35%, transparent 65%)",
      mixBlendMode: "overlay",
      opacity: "calc(0.04 + var(--o, 0) * 0.35)",
      transition: "opacity 200ms",
    }}
  />
);

/**
 * Single configurable foil/holo overlay for any card. Renders whatever the
 * `foil` config asks for: full-surface holo, a metallic/holo border ring, or
 * both. Pass `undefined`/`false` for a plain card. `seed` gives a stable,
 * varied texture when the config doesn't pin one.
 */
export const FoilOverlay = ({
  foil,
  seed,
}: {
  foil?: FoilConfig | boolean | undefined;
  seed: string;
}) => {
  if (!foil) return null;
  const cfg: FoilConfig = foil === true ? {} : foil;
  const tint: FoilTint = cfg.tint ?? "rainbow";
  // Border-only configs suppress the full-surface holo unless a texture is
  // explicitly requested.
  const borderOnly = (cfg.borderMetal || cfg.borderFoil) && cfg.texture === undefined;
  const texture: FoilTexture = cfg.texture ?? pickFoilTexture(seed);

  return (
    <>
      {!borderOnly && <FoilLayer texture={texture} tint={tint} />}
      {(cfg.borderMetal || cfg.borderFoil) && (
        <BorderFoilLayer metal={cfg.borderMetal} tint={tint} />
      )}
    </>
  );
};
